"use client";
import React, { useState } from "react";
import { Nft } from "@/api/nfts/types";
import { AuctionOrder } from "@/api/orders/types";

import { useMutation } from "@tanstack/react-query";
import { useTxIndexer } from "@/indexer/provider/context";
import usePurchaseSettleMutation from "./hooks/usePurchaseSettleMutation";
import { ContractTransactionResponse } from "ethers";
import useWithCorrectNetwork from "@/shared/hooks/useWithCorrectNetwork";
import useTotalTakerPrice from "@/shared/hooks/useTotalTakerPrice";
import useExchangeERC20ApprovalMutation from "@/web3/contracts/common/actions/useExchangeERC20ApprovalMutation";
import { NftCompletePurchaseDialog } from "../NftCompletePurchaseDialog";
import { NativeCurrencies } from "@/shared/constants";
import useAccountBalance from "@/shared/hooks/useAccountBalance";
import useOrdersInvalidation from "@/shared/hooks/useOrdersInvalidation";
import { useAuctionSellerGet } from "../../Sales/Forms/CreateFixedAuction/hooks/useSellerGet";

export interface Props {
  nft: Nft;
  auction: AuctionOrder;
  onSuccess: () => void;
  onClose: () => void;
}

function NftCompletePurchaseModal({ nft, auction, onSuccess, onClose }: Props) {
  const { totalPrice } = useTotalTakerPrice(auction);
  const [error, setError] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const { startIndexerSync } = useTxIndexer();

  const purchaseSettleMutation = usePurchaseSettleMutation(auction);

  const invalidateOrders = useOrdersInvalidation(nft.id);

  const onIndexed = async () => {
    // Add delay so that backend has time to delete finished auctions
    await invalidateOrders(3200);

    setIsLoading(false);

    onSuccess();
  };

  const { mutateAsync: settlePurchase } = useMutation({
    mutationFn: purchaseSettleMutation,
    onSuccess: (tx: ContractTransactionResponse) => {
      startIndexerSync({
        txHash: tx.hash,
        onIndexed,
      });
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const { approve } = useExchangeERC20ApprovalMutation(
    auction.paymentErc20TokenAddress,
  );

  const { balanceInCrypto } = useAccountBalance(
    auction.paymentErc20TokenAddress,
  );

  const checkedSettlePurchase = useWithCorrectNetwork(async () => {
    let fundsToSend;

    setIsLoading(true);

    if (!NativeCurrencies.includes(auction.paymentErc20TokenAddress)) {
      await approve(BigInt(totalPrice!.raw!));
      fundsToSend = "0";
    } else {
      fundsToSend = totalPrice!.raw!;
    }

    if (
      balanceInCrypto !== "-" &&
      (balanceInCrypto || balanceInCrypto === 0) &&
      totalPrice?.rounded &&
      +balanceInCrypto < +totalPrice.rounded
    ) {
      setError("Insufficient account balance");
      return;
    }

    await settlePurchase(fundsToSend);
  });
  const sellerGet = useAuctionSellerGet(
    nft.id,
    +auction.price!,
    auction.paymentErc20TokenAddress,
  );
  return (
    <NftCompletePurchaseDialog
      nft={nft}
      order={auction}
      sellerGet={sellerGet}
      onPurchase={checkedSettlePurchase}
      onClose={onClose}
      title="Complete purchase"
      buttonText="Buy artwork"
      isLoading={isLoading}
      error={error}
    />
  );
}

export default NftCompletePurchaseModal;
